import { useState, useEffect, useRef } from 'react';
import { Form, Input, message, Tooltip } from 'antd';
import LoadingButton from '@mui/lab/LoadingButton';
import { InfoCircleOutlined } from "@ant-design/icons";
import { backendGetRequest, backendGetRequestWithCache, backendPatchWithoutRefresh } from '../../helpers/callBackend';
import GenericModal from '../buttons/genericModal';
import SelectBankAccountTag from '../specific/customTags/selectBankAccountTag';
import SelectBankAccountPurpose from '../specific/customTags/selectBankAccountPurpose';

import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

function EditBankAccount(props) {
  const [visible, setVisible] = useState(false);
  const [placementUpdateLoading, setPlacementUpdateLoading] = useState(false);
  const [nameUpdateLoading, setNameUpdateLoading] = useState(false);

  const [flattenedTags, setFlattenedTags] = useState([]);
  const [flattenedTagsLoading, setFlattenedTagsLoading] = useState(true);
  const [flattenedTagsPageCountExpected, setFlattenedTagsPageCountExpected] = useState(0);
  const [flattenedTagsPageCountReceived, setFlattenedTagsPageCountReceived] = useState(-1);

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsPageCountExpected, setGroupsPageCountExpected] = useState(0);
  const [groupsPageCountReceived, setGroupsPageCountReceived] = useState(-1);

  const BA_TAG = 1;
  const formRef = useRef(null);

  useEffect(() => {
    backendGetRequest("/api/generic-tag/group/", { params: { page_index: 0, group_type: BA_TAG } })
      .then((res) => {
        setGroupsPageCountExpected(res?.data?.total_pages);
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          backendGetRequestWithCache("api/generic-tag/group/", { params: { page_index: i, group_type: BA_TAG } },
            (cachedResponse) => {
              const cachedGroups = cachedResponse?.data?.generic_groups || [];
              setGroups(prevState => {
                const updatedGroups = [...prevState];
                cachedGroups.forEach(group => {
                  if (!updatedGroups.some(existingGroup => existingGroup.id === group.id)) {
                    updatedGroups.push(group);
                  }
                });
                return updatedGroups;
              });
            },
            (nonCachedResponse) => {
              const nonCachedGroups = nonCachedResponse?.data?.generic_groups || [];
              setGroups(prevState => {
                const updatedGroups = [...prevState];
                nonCachedGroups.forEach(group => {
                  const index = updatedGroups.findIndex(existingGroup => existingGroup.id === group.id);
                  if (index !== -1) {
                    updatedGroups[index] = group;
                  } else {
                    updatedGroups.push(group);
                  }
                });
                return updatedGroups;
              });
              setGroupsPageCountReceived(prevState => prevState === -1 ? 1 : prevState + 1);
            },
            (err) => {
              setGroupsPageCountExpected(prevState => prevState - 1);
            }
          );
        }
      });

    backendGetRequest("/api/generic-tag/", { params: { page_index: 0, group_type: BA_TAG } })
      .then((res) => {
        setFlattenedTagsPageCountExpected(res?.data?.total_pages);
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          backendGetRequestWithCache("api/generic-tag/", { params: { page_index: i, group_type: BA_TAG } },
            (cachedResponse) => {
              const cachedTags = cachedResponse?.data?.generic_tags || [];
              setFlattenedTags(prevState => {
                const updatedTags = [...prevState];
                cachedTags.forEach(tag => {
                  if (!updatedTags.some(existingTag => existingTag.id === tag.id)) {
                    updatedTags.push(tag);
                  }
                });
                return updatedTags;
              });
            },
            (nonCachedResponse) => {
              const nonCachedTags = nonCachedResponse?.data?.generic_tags || [];
              setFlattenedTags(prevState => {
                const updatedTags = [...prevState];
                nonCachedTags.forEach(tag => {
                  const index = updatedTags.findIndex(existingTag => existingTag.id === tag.id);
                  if (index !== -1) {
                    updatedTags[index] = tag;
                  } else {
                    updatedTags.push(tag);
                  }
                });
                return updatedTags;
              });
              setFlattenedTagsPageCountReceived(prevState => prevState === -1 ? 1 : prevState + 1);
            },
            (err) => {
              setFlattenedTagsPageCountExpected(prevState => prevState - 1);
            }
          );
        }
      });
  }, []);

  useEffect(() => {
    if (!flattenedTagsPageCountExpected) return;
    setFlattenedTagsLoading(flattenedTagsPageCountExpected > flattenedTagsPageCountReceived);
  }, [flattenedTagsPageCountExpected, flattenedTagsPageCountReceived]);

  useEffect(() => {
    if (!groupsPageCountExpected) return;
    setGroupsLoading(groupsPageCountExpected > groupsPageCountReceived);
  }, [groupsPageCountExpected, groupsPageCountReceived]);

  const onHierarchyUpdate = (tagIds) => {
    setPlacementUpdateLoading(true);
    const data = {
      account_id: props.accountId,
      generic_tags: tagIds,
      attributes: true,
    }
    backendPatchWithoutRefresh("/api/bank-account/reassignment/", data)
      .then((response) => {
        message.success("Bank account placement updated successfully.");
        props.onSuccessReassign(response.data.data);
        setPlacementUpdateLoading(false);
      }).catch((error) => {
        setPlacementUpdateLoading(false);
      })
  };

  const onPurposeUpdate = (purposeId) => {
    const data = {
      account_id: props.accountId,
      purpose_id: purposeId,
    }
    backendPatchWithoutRefresh("/api/bank-account/reassignment/", data)
      .then((response) => {
        message.success("Bank account purpose updated successfully.");
      }).catch((error) => {
        console.error(error)
      })
  };

  const onNameUpdate = (values) => {
    setNameUpdateLoading(true);
    var data = values;
    data['account_id'] = props.accountId;
    data['action'] = "edit_account";
    backendPatchWithoutRefresh("/api/bank-account/", data)
      .then((response) => {
        message.success("Bank account name updated successfully.")
        props.onSuccessRename(props.accountId, values.description);
        setNameUpdateLoading(false);
      }).catch((error) => {
        setNameUpdateLoading(false);
      })
  };

  return (
    <GenericModal
      size="md"
      visible={visible}
      handleCancelModal={() => {
        if (formRef.current) {
          formRef.current.resetFields();
        }
        setVisible(false);
      }}
      showModal={() => setVisible(true)}
      button={<span style={{ color: "#1890ff" }}>Edit </span>}
      modalTitle={`Edit ${props.accountDescription}`}
      modalBody={
        <Form
          layout="vertical"
          name="name"
          onFinish={onNameUpdate}
          autoComplete="off"
          ref={formRef}
        >
          <Stack direction="column" spacing={1}>

            <Tooltip title="The name of the account is as you see in the dashboard. You will still have the name of the account as it appears in your bank portal accessible.">
              <Typography color="secondary">
                Name
                <InfoCircleOutlined style={{ marginLeft: '5px' }} />
              </Typography>
            </Tooltip>
            <Stack direction="row" spacing={2}>

              <Form.Item
                name="description"
                required={true}
                initialValue={props.accountDescription}
                style={{ width: "100%" }}
                rules={[
                  {
                    required: true,
                    message: "Please enter an account name",
                  },
                ]}
              >
                <Input initialValue={props.accountDescription} />
              </Form.Item>
              <LoadingButton
                disableElevation size="small" type="submit" variant="contained" color="primary"
                loading={nameUpdateLoading}
                loadingPosition="start"
                sx={{ fontSize: 14, textTransform: 'none' }}
              >
                Apply
              </LoadingButton>

            </Stack>
            <Typography color="secondary">
              Hierarchy Tags
            </Typography>
            <SelectBankAccountTag
              groups={groups}
              customTags={flattenedTags}
              loading={flattenedTagsLoading || groupsLoading || placementUpdateLoading}
              placeholder={"Select tags to associated with this account"}
              handleApply={onHierarchyUpdate}
              includeUncategorized={false}
              multiSelect={true}
              required={true}
              currentTagIds={props.accountGenericTags}
            />
            <Typography color="secondary">
              Purpose
            </Typography>
            <SelectBankAccountPurpose
              placeholder={"Select purpose of account"}
              onSubmit={onPurposeUpdate}
              currentPurposeId={props.accountPurposeId}
            />


          </Stack>
        </Form>
      }
    />
  )
}

export default EditBankAccount;
